import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
// import { useSnackbar } from 'notistack';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import clsx from 'clsx';

import { AxiosError } from 'axios';
import { getInstitutionInfoByUrlExtension } from '../../requests/api/institutions';

// import handleApiResponse from '../../utils/handleApiResponse';

import Page from '../../components/Layout/Page';
import Logo from '../../components/Layout/Logo';
import LoginForm from './LoginForm';

import useStyles from './styles';
import UserInformationForm from './UserInformationForm';
import { Institution, InstitutionUser } from './types';
import parseToLocalDate from '../../utils/dates';

// Debe coincidir con INSTITUTION_HAS_NO_ACTIVE_SESSION (server/misc/const/errors.ts)
const NO_ACTIVE_SESSION_MESSAGE = 'La institución no tiene una jornada activa';

function LoginInstitutionsView() {
  const classes = useStyles();
  // const { enqueueSnackbar } = useSnackbar();
  const { institutionUrlExtension } = useParams<{ institutionUrlExtension: string }>();

  const initialValues = {
    id: null,
    role: '',
    rut: '',
    password: '',
    name: null,
    lastname: null,
    email: null,
    phoneNumber: null,
    school: null,
    schoolYear: null,
    preferredUniversity: null,
    preferredCarrer: null,
    deleted: false,
  };

  const [institutionUser, setInstitutionUser] = useState<InstitutionUser>(initialValues);
  // Nombre de la institución cuando existe pero no tiene jornada activa
  const [inactiveInstitutionName, setInactiveInstitutionName] = useState<string | null>(null);

  const [institution, setInstitution] = useState<Institution>({
    id: null,
    name: '',
    urlExtension: institutionUrlExtension,
    logoUrl: '',
    institutionSessions: [{
      startDate: null,
      endDate: null,
      resultsDate: null,
    }],
  });

  useEffect(() => {
    const getInstitutionInfo = async () => {
      try {
        const response = await getInstitutionInfoByUrlExtension({ institutionUrlExtension });
        if (response.success) {
          if (typeof response.institution.institutionSessions[0].startDate === 'string') {
            response.institution.institutionSessions[0].startDate = parseToLocalDate(
              response.institution.institutionSessions[0].startDate,
            );
          }
          if (typeof response.institution.institutionSessions[0].endDate === 'string') {
            response.institution.institutionSessions[0].endDate = parseToLocalDate(
              response.institution.institutionSessions[0].endDate,
            );
          }
          if (typeof response.institution.institutionSessions[0].resultsDate === 'string') {
            response.institution.institutionSessions[0].resultsDate = parseToLocalDate(
              response.institution.institutionSessions[0].resultsDate,
            );
          }

          setInstitution(response.institution);
        }
      } catch (err) {
        const e = err as AxiosError;
        if (e.response?.data?.message === NO_ACTIVE_SESSION_MESSAGE) {
          setInactiveInstitutionName(e.response.data.institution?.name || '');
        }
      }
    };

    if (institutionUrlExtension && !institution.id) {
      getInstitutionInfo();
    }
  }, []);

  if (!institution.id) {
    return (
      <Page className={classes.noInstitutionRoot} title="Ingreso">
        {/* <Container className={classes.imageContainer}> */}
        {/* <Box className={classes.blueBox} /> */}
        <Box className={classes.infoContainer}>
          <Box className={classes.noInstitutionMessageContainer}>
            <Logo />
            {inactiveInstitutionName !== null ? (
              <Typography className={classes.noInstitutionMessage}>
                {inactiveInstitutionName || 'La institución'} no tiene una jornada activa
                en este momento.
                <br />
                Por favor, intenta más tarde o contacta a tu institución.
              </Typography>
            ) : (
              <Typography className={classes.noInstitutionMessage}>
                La institución a la que intenta acceder no existe.
                <br />
                Por favor, revise la URL e intente nuevamente.
              </Typography>
            )}
          </Box>
        </Box>
        {/* </Container> */}
      </Page>
    );
  }

  return (
    <Page className={classes.root} title="Ingreso">
      {/* <Container className={classes.imageContainer}> */}
      {/* <Box className={classes.blueBox} /> */}
      <Box className={clsx(classes.infoContainer, classes.infoSide)}>
        <Box className={classes.loginTitle}>
          Bienvenido a la plataforma
          <br /> de rendición de ensayos de <br />
          <span>{institution.name}</span>
        </Box>
        <Box>
          <Typography className={classes.loginParagraph}>
            En la UAI queremos apoyar el proceso de orientación y preparación para
            la Prueba de Acceso a la Educación Superior (PAES) de los estudiantes,
            para un ingreso exitoso a la Universidad en Admisión 2027.
          </Typography>
          <br />
          <Typography className={classes.loginParagraph}>
            ENSAYO PAES UAI ONLINE
          </Typography>
          <Typography className={classes.loginParagraph}>
            Dirigido a estudiantes que hayan realizado previamente su inscripción en el
            Ensayo PAES UAI Online. El Ensayo PAES UAI Online estará disponible desde el
            lunes 9 de noviembre, a las 12:00 horas, hasta el domingo 29 de noviembre, a
            las 23:59 horas. Los resultados incluirán el puntaje obtenido y la cantidad de
            preguntas correctas, incorrectas y omitidas. Además, se podrá visualizar el
            solucionario.
          </Typography>
          <br />
          <Typography className={classes.loginParagraph}>
            RESULTADOS ENSAYO PAES UAI
            <br />
            Modalidad presencial en los campus de Peñalolén (Santiago) y Viña del Mar
          </Typography>
          <Typography className={classes.loginParagraph}>
            En este sitio podrás revisar los resultados del Ensayo PAES rendido
            presencialmente el sábado 7 de noviembre de 2026. Los resultados estarán
            disponibles desde el viernes 13 de noviembre hasta el martes 1 de diciembre
            de 2026, a las 23:59 horas. Se entregarán los resultados con el puntaje
            obtenido y la cantidad de preguntas correctas, incorrectas y omitidas.
            Además, se podrá visualizar el solucionario.
          </Typography>
          <Typography className={classes.loginParagraph}>
            ¡Mucho éxito! Esperamos contar con tu presencia en nuestras próximas actividades.
          </Typography>
        </Box>
      </Box>
      {/* </Container> */}
      {/* Renders the login form */}
      {institutionUser && !institutionUser.id ? (
        <Container className={classes.formContainer}>
          <Box className={classes.logosContainer}>
            <Logo />
            <img alt="Institution logo" src={institution.logoUrl} />
          </Box>
          <Box className={classes.inputsContainer}>
            <Typography className={classes.loginMessage}>
              Inicia sesión para utilizar la plataforma
            </Typography>
            <LoginForm
              institutionId={institution.id}
              urlExtension={institutionUrlExtension}
              setInstitutionUser={setInstitutionUser}
            />
          </Box>
        </Container>
      ) : null}
      {/* Renders the institution user information form */}
      {institutionUser && institutionUser.id && !institutionUser.name && !institutionUser.email ? (
        <Container className={classes.formContainer}>
          <Box className={classes.inputsContainer}>
            <Typography className={classes.institutionUserMessage}>
              Ingresa tu información académica
            </Typography>
            <UserInformationForm
              institutionUserId={institutionUser.id}
              initialValues={initialValues}
              setInstitutionUser={setInstitutionUser}
              urlExtension={institutionUrlExtension}
            />
          </Box>
        </Container>
      ) : null}
    </Page>
  );
}

export default LoginInstitutionsView;
